import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, Spinner, Switch, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { settingsApi } from './settingsApi';

let _uid = 0;
const makeKey = () => `tmp-${Date.now()}-${_uid++}`;

const toRows = (list) =>
  (list || []).map((o) => ({
    key: o.id || makeKey(),
    id: o.id || '',
    code: o.code || '',
    name: o.name || '',
    isActive: o.isActive !== false,
  }));

export default function CodingTab() {
  const toast = useToast();
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsApi.getCoding();
      setCostCenters(toRows(res?.costCenters));
      setGlAccounts(toRows(res?.glAccounts));
    } catch (err) {
      toast.error(t('Could not load coding settings'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  const setRows = (which) => (which === 'cc' ? setCostCenters : setGlAccounts);
  const rowsFor = (which) => (which === 'cc' ? costCenters : glAccounts);

  const update = (which, key, patch) =>
    setRows(which)((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = (which) =>
    setRows(which)((rows) => [...rows, { key: makeKey(), id: '', code: '', name: '', isActive: true }]);
  const removeRow = (which, key) =>
    setRows(which)((rows) => rows.filter((r) => r.key !== key));

  const save = async () => {
    const clean = (rows) =>
      rows
        .filter((r) => r.code.trim() || r.name.trim())
        .map((r) => ({ id: r.id || '', code: r.code.trim(), name: r.name.trim(), isActive: r.isActive }));
    setSaving(true);
    try {
      const res = await settingsApi.updateCoding({
        costCenters: clean(costCenters),
        glAccounts: clean(glAccounts),
      });
      setCostCenters(toRows(res?.costCenters));
      setGlAccounts(toRows(res?.glAccounts));
      toast.success(t('Coding chart saved'));
    } catch (err) {
      toast.error(t('Could not save coding settings'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  const renderList = (which, title, hint, codePlaceholder, namePlaceholder) => {
    const rows = rowsFor(which);
    return (
      <Card>
        <Card.Body>
          <div className="set-section__head">
            <div>
              <h3 className="set-section__title">{title}</h3>
              <p className="set-section__hint">{hint}</p>
            </div>
          </div>
          <div className="set-rows">
            {rows.length === 0 && <span className="set-inherit">{t('None yet. Add one below.')}</span>}
            {rows.map((r) => (
              <div key={r.key} className="set-row set-row--coding">
                <Input value={r.code} onChange={(e) => update(which, r.key, { code: e.target.value })} placeholder={codePlaceholder} aria-label={t('Code')} />
                <Input value={r.name} onChange={(e) => update(which, r.key, { name: e.target.value })} placeholder={namePlaceholder} aria-label={t('Name')} />
                <Switch checked={r.isActive} onChange={(on) => update(which, r.key, { isActive: on })} label={t('Active')} />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(which, r.key)} aria-label={t('Remove')}>
                  <Icon.Trash size={15} />
                </Button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Button type="button" variant="secondary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={() => addRow(which)}>
              {which === 'cc' ? t('Add cost centre') : t('Add GL account')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="set-form">
      {renderList('cc', t('Cost centres'), t('Departments / outlets that spend is attributed to. Used for coding and budgets.'), 'FNB', t('Food & Beverage'))}
      {renderList('gl', t('GL accounts'), t('Chart of expense accounts document lines can post to.'), '5010', t('Food purchases'))}
      <div className="set-form__actions">
        <Button type="button" variant="primary" loading={saving} onClick={save}>{t('Save changes')}</Button>
      </div>
    </div>
  );
}
